import React, { useRef, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import './settings-templates.css';

const { FiImage, FiMail, FiPhone, FiSave, FiUploadCloud } = FiIcons;

function AppSettings({ settings, onSave }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState(settings);
  const [saved, setSaved] = useState(false);

  const update = (key, value) => {
    setSaved(false);
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => update('logo', reader.result);
    reader.readAsDataURL(file);
  };

  const save = (event) => {
    event.preventDefault();
    onSave(form);
    setSaved(true);
  };

  return (
    <main className="main-content settings-page">
      <div className="settings-page-heading">
        <div>
          <span className="eyebrow">Workspace preferences</span>
          <h1>Settings</h1>
          <p>Manage your business details and portal identity.</p>
        </div>
      </div>

      <form className="settings-layout" onSubmit={save}>
        <section className="panel settings-card">
          <div className="settings-card-heading">
            <div className="settings-section-icon"><SafeIcon icon={FiImage} /></div>
            <div>
              <span className="eyebrow">Brand identity</span>
              <h2>App details</h2>
              <p>These details appear in the portal header and client communications.</p>
            </div>
          </div>

          <div className="settings-logo-row">
            <div className="settings-logo-preview">
              {form.logo ? <img src={form.logo} alt="App logo preview" /> : <SafeIcon icon={FiImage} />}
            </div>
            <div className="settings-logo-copy">
              <strong>Business logo</strong>
              <p>Upload a PNG, JPG, or SVG logo for the portal header.</p>
              <button type="button" className="button secondary" onClick={() => fileRef.current?.click()}>
                <SafeIcon icon={FiUploadCloud} /> Choose logo
              </button>
              <input
                ref={fileRef}
                type="file"
                hidden
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={handleLogo}
              />
            </div>
          </div>

          <div className="settings-divider" />
          <div className="settings-form-grid">
            <label>
              App name
              <input value={form.appName} onChange={(event) => update('appName', event.target.value)} required />
              <small>Shown beside the logo in the portal header.</small>
            </label>
            <label>
              Phone number
              <span className="settings-input">
                <SafeIcon icon={FiPhone} />
                <input value={form.phone} onChange={(event) => update('phone', event.target.value)} required />
              </span>
              <small>Used for the header contact link.</small>
            </label>
            <label>
              Email address
              <span className="settings-input">
                <SafeIcon icon={FiMail} />
                <input type="email" value={form.email} onChange={(event) => update('email', event.target.value)} required />
              </span>
              <small>Used for client communication and support.</small>
            </label>
          </div>
        </section>

        <aside className="settings-help-card">
          <span className="eyebrow">Quick guide</span>
          <h2>Keep your portal consistent</h2>
          <p>Use your business name, logo, phone number, and monitored email so clients know how to reach your team.</p>
          <div className="settings-help-list">
            <span><b>01</b> Upload a recognizable logo</span>
            <span><b>02</b> Confirm the contact details</span>
            <span><b>03</b> Save changes to update the portal</span>
          </div>
        </aside>

        <div className="settings-actions">
          {saved && <span className="settings-saved">Saved on this device</span>}
          <button className="button primary"><SafeIcon icon={FiSave} /> Save changes</button>
        </div>
      </form>
    </main>
  );
}

export default AppSettings;